const BASE_URL = process.env.NEXT_PUBLIC_APP_URL ?? "https://vident.dev"

type JsonLdProps = {
	data: Record<string, unknown>
}

export function JsonLd({ data }: JsonLdProps) {
	return (
		<script
			type="application/ld+json"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires innerHTML for structured data
			dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
		/>
	)
}

export function OrganizationJsonLd() {
	const data = {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: "Vident",
		url: BASE_URL,
		logo: `${BASE_URL}/icon-512.png`,
		description:
			"Frontend error tracking, session replay, and Web Vitals monitoring for developers.",
		sameAs: ["https://github.com/vident"],
		contactPoint: {
			"@type": "ContactPoint",
			contactType: "customer support",
			email: "support@vident.dev",
		},
	}
	return <JsonLd data={data} />
}

export function WebSiteJsonLd() {
	const data = {
		"@context": "https://schema.org",
		"@type": "WebSite",
		name: "Vident",
		url: BASE_URL,
		description:
			"Frontend error tracking, session replay, and Web Vitals monitoring for developers.",
		potentialAction: {
			"@type": "SearchAction",
			target: {
				"@type": "EntryPoint",
				urlTemplate: `${BASE_URL}/docs?q={search_term_string}`,
			},
			"query-input": "required name=search_term_string",
		},
	}
	return <JsonLd data={data} />
}

export function SoftwareApplicationJsonLd() {
	const data = {
		"@context": "https://schema.org",
		"@type": "SoftwareApplication",
		name: "Vident",
		applicationCategory: "DeveloperApplication",
		operatingSystem: "Web",
		url: BASE_URL,
		description:
			"Frontend monitoring platform with error tracking, session replay, Web Vitals, and alerting.",
		offers: [
			{
				"@type": "Offer",
				name: "Free",
				price: "0",
				priceCurrency: "USD",
				description: "5K sessions/mo, 7-day retention",
			},
			{
				"@type": "Offer",
				name: "Starter",
				price: "9",
				priceCurrency: "USD",
				priceSpecification: {
					"@type": "UnitPriceSpecification",
					price: "9",
					priceCurrency: "USD",
					billingDuration: "P1M",
				},
				description: "10K sessions/mo, 30-day retention, alerting",
			},
			{
				"@type": "Offer",
				name: "Pro",
				price: "29",
				priceCurrency: "USD",
				priceSpecification: {
					"@type": "UnitPriceSpecification",
					price: "29",
					priceCurrency: "USD",
					billingDuration: "P1M",
				},
				description: "50K sessions/mo, 90-day retention, alerting",
			},
			{
				"@type": "Offer",
				name: "Business",
				price: "79",
				priceCurrency: "USD",
				priceSpecification: {
					"@type": "UnitPriceSpecification",
					price: "79",
					priceCurrency: "USD",
					billingDuration: "P1M",
				},
				description: "200K sessions/mo, 180-day retention, alerting",
			},
		],
	}
	return <JsonLd data={data} />
}

type BreadcrumbItem = {
	name: string
	href: string
}

type BreadcrumbJsonLdProps = {
	items: BreadcrumbItem[]
}

export function BreadcrumbJsonLd({ items }: BreadcrumbJsonLdProps) {
	const data = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: items.map((item, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: item.name,
			item: `${BASE_URL}${item.href}`,
		})),
	}
	return <JsonLd data={data} />
}

type FAQItem = {
	question: string
	answer: string
}

type FAQJsonLdProps = {
	items: FAQItem[]
}

export function FAQJsonLd({ items }: FAQJsonLdProps) {
	const data = {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: items.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answer,
			},
		})),
	}
	return <JsonLd data={data} />
}
